import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material"
import {
    KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
    KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon
} from "@mui/icons-material";
import { green, grey } from "@mui/material/colors";

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";
import { BasicLoader, IconButton, Typography } from "@initia-digital/component-library"

import { SectionHeader } from "./SectionHeader"
import { useWorkAndSafetyStore } from "../store";
import { usePlanDetail } from "../hooks";
import { PlanSummary } from "./PlanSummary";
import { DiscussionTab } from "./DiscussionTab";
import { IPrestartStageProps } from "../interfaces/componentInterfaces";


export const ReviewThePlans = (props: IPrestartStageProps) => {
    // ------------------------------------
    const {
        editPrestartState,
    } = useWorkAndSafetyStore();
    const {
        leadingTeam
    } = editPrestartState;

    const [planReviewTab, setPlanReviewTab] = useState<"DISCUSSION" | "OUR_PLAN">("DISCUSSION");

    const params = useParams();
    const { planId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    // PLAN DETAIL
    const { planDetailData, planDetailLoading } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));


    return (
        <Box
            id="review-the-plans"
            display="flex" flexDirection="column"
            border={isSmallerThanSm ? "none" : "1px solid lightgrey"}
            borderTop={`1px solid ${theme.palette.secondary.main}`} borderRadius={isSmallerThanSm ? "none" : "5px"}
            pb="20px" px={isSmallerThanSm ? '10px' : '20px'}
            mt={isSmallerThanSm ? '20px' : '0px'} pt={isSmallerThanSm ? '20px' : '0px'}
        >
            <Box display="flex" flexDirection="row" alignItems="center">
                <SectionHeader label="2. Review the plans" />
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#hazard-observations'}
                            tooltipTitle={`Go to Hazard Observations`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowDownIcon />
                        </IconButton>
                    </Box>
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#prestart-start'}
                            tooltipTitle={`Go to Start`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowUpIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="row" alignItems="center"
                px="10px"
            >
                <Typography
                    fontSize="0.8rem"
                    fontWeight={300}
                    color={theme.palette.primary.contrastText}
                >
                    {`Step back from the work area to complete this ${leadingTeam ? 'prestart' : 'Take-5'} before you get started.`}
                </Typography>
            </Box>
            <Box>
                <Box mt="15px" px="10px" display="flex" flexDirection="row">
                    <Box
                        bgcolor={planReviewTab === 'DISCUSSION' ? green['A700'] : grey['400']}
                        color="white" px="15px" py="5px" borderRadius="8px 8px 0px 0px"
                        sx={{ ':hover': { cursor: 'pointer' } }}
                        onClick={() => setPlanReviewTab("DISCUSSION")}
                    >
                        <strong>
                            { leadingTeam ? `Discussion` : `Review` }
                        </strong>
                    </Box>
                    <Box
                        bgcolor={planReviewTab === 'OUR_PLAN' ? green['A700'] : grey['400']}
                        color="white" px="15px" py="5px" borderRadius="8px 8px 0px 0px"
                        sx={{ ':hover': { cursor: 'pointer' } }}
                        onClick={() => setPlanReviewTab("OUR_PLAN")}
                    >
                        <strong>Our Plan</strong>
                    </Box>
                </Box>
                <Box px="10px" mb="10px">
                    <Box border={`2px solid ${green['A700']}`} borderRadius="0px 10px 10px 10px">
                        {
                            planDetailLoading &&
                            <Box px="5px" display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt="15px">
                                <Typography fontSize="0.8rem">Loading plan detail...</Typography>
                                <BasicLoader
                                    size={50}
                                />
                            </Box>
                        }
                        {/* DISCUSSION TAB */}
                        {
                            planReviewTab === 'DISCUSSION' && !planDetailLoading && baseDefinedAndNotNullNarrowing(planDetailData) &&
                            <Box py={isSmallerThanSm ? "10px" : "14px"} px={isSmallerThanSm ? "10px" : "20px"}>
                                <DiscussionTab
                                    {...props}
                                />
                            </Box>
                        }

                        {/* OUR PLAN TAB */}
                        {
                            planReviewTab === 'OUR_PLAN' && !planDetailLoading && baseDefinedAndNotNullNarrowing(planDetailData) &&
                            <Box py={isSmallerThanSm ? "10px" : "14px"} px={isSmallerThanSm ? "10px" : "20px"}>
                                <PlanSummary />
                            </Box>
                        }
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}